// This file is automatically generated. Do not edit it directly.
import {
  createClient,
  navigatorLock,
  processLock,
  type LockFunc,
} from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const SUPABASE_CLIENT_KEY = SUPABASE_PUBLISHABLE_KEY || SUPABASE_ANON_KEY;
const FALLBACK_SUPABASE_URL = 'https://invalid.localhost';
const FALLBACK_SUPABASE_KEY = 'invalid-anon-key';
const RESOLVED_SUPABASE_URL = SUPABASE_URL ?? FALLBACK_SUPABASE_URL;

function resolveProjectRef(url: string): string {
  try {
    return new URL(url).hostname.split('.')[0] || 'local';
  } catch {
    return 'local';
  }
}

export const SUPABASE_AUTH_STORAGE_KEY = `sb-${resolveProjectRef(RESOLVED_SUPABASE_URL)}-auth-token`;
export const LEGACY_SUPABASE_AUTH_STORAGE_KEY = 'supabase.auth.token';

const memoryStorage = new Map<string, string>();

function safeStorageGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return memoryStorage.get(key) ?? null;
  }
}

function safeStorageSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    memoryStorage.set(key, value);
  }
}

function safeStorageRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    memoryStorage.delete(key);
  }
}

const safeBrowserStorage = {
  getItem: safeStorageGetItem,
  setItem: safeStorageSetItem,
  removeItem: safeStorageRemoveItem,
};

const NAVIGATOR_LOCK_TIMEOUT_MS = 2500;
const PROCESS_LOCK_TIMEOUT_MS = 30000;
const DISABLE_NAVIGATOR_LOCK_STORAGE_KEY = 'soctiv:disable-navigator-lock';

function isStandalonePwaRuntime(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  } catch {
    // Ignore unsupported matchMedia environments.
  }

  return Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
}

const disableNavigatorLockForCurrentRuntime = (
  isStandalonePwaRuntime()
  || safeStorageGetItem(DISABLE_NAVIGATOR_LOCK_STORAGE_KEY) === '1'
);

if (disableNavigatorLockForCurrentRuntime) {
  safeStorageSetItem(DISABLE_NAVIGATOR_LOCK_STORAGE_KEY, '1');
}

let shouldUseNavigatorLock = (
  !disableNavigatorLockForCurrentRuntime
  && typeof globalThis !== 'undefined'
  && !!globalThis.navigator?.locks
);

function isAcquireTimeoutError(error: unknown): error is { isAcquireTimeout: true } {
  return (
    typeof error === 'object'
    && error !== null
    && 'isAcquireTimeout' in error
    && (error as { isAcquireTimeout?: unknown }).isAcquireTimeout === true
  );
}

const resilientAuthLock: LockFunc = async (name, acquireTimeout, fn) => {
  const timeout = acquireTimeout > 0 ? acquireTimeout : NAVIGATOR_LOCK_TIMEOUT_MS;

  if (shouldUseNavigatorLock) {
    try {
      return await navigatorLock(name, timeout, fn);
    } catch (error) {
      if (!isAcquireTimeoutError(error)) {
        throw error;
      }

      shouldUseNavigatorLock = false;
      safeStorageSetItem(DISABLE_NAVIGATOR_LOCK_STORAGE_KEY, '1');
      console.warn(
        `[Supabase Client] Navigator lock timed out for "${name}". Falling back to process lock for this app session.`,
        error
      );
    }
  }

  return processLock(name, PROCESS_LOCK_TIMEOUT_MS, fn);
};

console.debug('[Supabase Client] URL:', SUPABASE_URL);
console.debug('[Supabase Client] Key ends with:', SUPABASE_CLIENT_KEY?.slice(-20));

let configError: string | null = null;
if (!SUPABASE_URL || !SUPABASE_CLIENT_KEY) {
  configError =
    'Missing Supabase env vars. Set VITE_SUPABASE_URL and either VITE_SUPABASE_PUBLISHABLE_KEY or VITE_SUPABASE_ANON_KEY.';
}

if (!configError && /^sb_secret_/i.test(SUPABASE_CLIENT_KEY)) {
  configError = 'Supabase service role key detected in client config. Use a publishable anon key.';
}

if (configError) {
  console.error('[Supabase Client] Configuration error:', configError);
}

// Migrate old auth storage key once so existing sessions survive.
if (SUPABASE_AUTH_STORAGE_KEY !== LEGACY_SUPABASE_AUTH_STORAGE_KEY) {
  const hasCurrent = safeStorageGetItem(SUPABASE_AUTH_STORAGE_KEY);
  const legacyValue = safeStorageGetItem(LEGACY_SUPABASE_AUTH_STORAGE_KEY);
  if (!hasCurrent && legacyValue) {
    safeStorageSetItem(SUPABASE_AUTH_STORAGE_KEY, legacyValue);
  }
}

export const supabaseConfigError = configError;
export const isSupabaseConfigured = !configError;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(
  RESOLVED_SUPABASE_URL,
  SUPABASE_CLIENT_KEY ?? FALLBACK_SUPABASE_KEY,
  {
  auth: {
    storage: safeBrowserStorage,
    storageKey: SUPABASE_AUTH_STORAGE_KEY,
    persistSession: true,
    autoRefreshToken: true,
    lock: resilientAuthLock,
  },
});
